import { IndentDecrease, IndentIncrease, ListOrdered } from "lucide-react";
import type { BlockControl } from "@/components/ui/StructuredText";
import type { TextBlock } from "@/lib/structured-text";

/** A claim's words go no deeper than a sub-element, which is where the design stops indenting. */
export const DEEPEST_LINE = 2;

/**
 * The most lines a claim holds, which the store settles: `MAX_CLAIM_LINES` refuses a longer one.
 *
 * Said here as well so the editor refuses the line rather than taking it and losing it at the
 * save, which is the attorney finding their words gone with nothing having said why.
 */
export const MOST_LINES = 40;

/** The line the design draws at that depth, held once so the bar and the keys agree. */
function stepped(block: TextBlock, by: number) {
  return {
    depth: Math.min(DEEPEST_LINE, Math.max(0, block.depth + by)),
    numbered: block.numbered,
  };
}

/**
 * The ways a claim's line can be set, offered on the editor's bar.
 *
 * A limitation steps in under the preamble, and a set of alternatives within one
 * limitation is numbered i), ii), iii). Neither changes a word: they say what the
 * line is, and the attorney's prose is theirs.
 */
export const CLAIM_CONTROLS: readonly BlockControl[] = [
  {
    id: "indent",
    group: "depth",
    label: "Indent",
    shortcut: "Tab",
    icon: <IndentIncrease aria-hidden />,
    enabled: (block) => block.depth < DEEPEST_LINE,
    run: (block) => stepped(block, 1),
  },
  {
    id: "outdent",
    group: "depth",
    label: "Outdent",
    shortcut: "Shift+Tab",
    icon: <IndentDecrease aria-hidden />,
    enabled: (block) => block.depth > 0,
    run: (block) => stepped(block, -1),
  },
  {
    id: "numbered",
    group: "marker",
    label: "Number the line",
    // The bar is drawn only once the attorney is in the words, and the words
    // spend Tab on the indent, so without a key of its own this act could be
    // reached by the mouse and by nothing else.
    shortcut: "Mod+Shift+7",
    icon: <ListOrdered aria-hidden />,
    // Only a line that stands under something. A preamble carries no marker by
    // the rule the whole claim is lettered under, so numbering one stored a
    // flag that could never be drawn: the control did nothing, visibly.
    enabled: (block) => block.depth > 0,
    active: (block) => block.numbered,
    run: (block) => ({ depth: block.depth, numbered: !block.numbered }),
  },
];

/** How the keyboard does the same, said once at the end of the bar. */
export const CLAIM_SHORTCUTS =
  "Tab / Shift+Tab to indent · Enter for a new line";
